import React from "react";
import { motion } from "framer-motion";
import { Lock, Check, Sparkles, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

export const QuestList = ({ quests, xp, onRefresh, plan }) => {
  const complete = async (q) => {
    if (!q.unlocked) {
      if (q.locked_reason === "paid") {
        toast("Locked — upgrade to Quester to unlock 8 deeper quests.", {
          description: "Tap Upgrade to subscribe.",
          action: { label: "Upgrade", onClick: () => (window.location.href = "/pricing") },
        });
      } else {
        toast("Complete the previous quest first.");
      }
      return;
    }
    if (q.completed) return;
    try {
      const { data } = await api.post(`/quests/${q.quest_id}/complete`);
      toast.success(`+${data.awarded} XP — ${q.title}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete quest");
    }
  };

  return (
    <div data-testid="quest-list" className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#94A3B8]">Daily Quests</p>
          <h3 className="font-serif-mystic text-2xl">Your Path Today</h3>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-[0.25em] text-[#94A3B8]">XP</p>
          <p className="font-serif-mystic text-3xl gold-shimmer" data-testid="user-xp">
            {xp}
          </p>
        </div>
      </div>

      {quests.map((q, idx) => (
        <motion.div
          key={q.quest_id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.04 }}
          data-testid={`quest-card-${q.quest_id}`}
          className={`relative rounded-xl border p-4 transition-all duration-300 ${
            q.unlocked
              ? "bg-[#111625]/80 border-white/10 hover:border-[#D4AF37]/40"
              : "bg-[#0A0E17]/60 border-white/5"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">
                  Quest {q.order}
                </span>
                <span className="text-xs text-[#64748B]">·</span>
                <span className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                  {q.category}
                </span>
                {q.tier === "paid" && (
                  <span className="text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/20">
                    Quester
                  </span>
                )}
              </div>
              <h4
                className={`font-serif-mystic text-xl ${q.unlocked ? "text-white" : "text-[#64748B]"}`}
              >
                {q.title}
              </h4>
              <p className={`text-sm mt-1 ${q.unlocked ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                {q.description}
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs text-[#64748B]">
                <span className="flex items-center gap-1">
                  <Clock size={12} strokeWidth={1.5} /> {q.duration_min} min
                </span>
                <span className="flex items-center gap-1">
                  <Sparkles size={12} strokeWidth={1.5} className="text-[#D4AF37]" /> {q.xp} XP
                </span>
              </div>
            </div>
            <div>
              {q.completed ? (
                <div className="text-[#2ED199] flex items-center gap-1 text-xs" data-testid={`quest-done-${q.quest_id}`}>
                  <Check size={16} strokeWidth={2} /> Done
                </div>
              ) : !q.unlocked ? (
                <div className="text-[#94A3B8] flex items-center gap-1 text-xs" data-testid={`quest-locked-${q.quest_id}`}>
                  <Lock size={14} strokeWidth={1.5} />
                  {q.locked_reason === "paid" ? "Upgrade" : "Locked"}
                </div>
              ) : (
                <Button
                  onClick={() => complete(q)}
                  data-testid={`quest-complete-btn-${q.quest_id}`}
                  size="sm"
                  className="bg-transparent border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  Begin
                </Button>
              )}
            </div>
          </div>
          {!q.unlocked && q.locked_reason === "paid" && (
            <div className="absolute inset-0 rounded-xl pointer-events-none backdrop-blur-[1px] bg-black/10" />
          )}
        </motion.div>
      ))}

      {plan === "free" && (
        <div
          data-testid="upgrade-card"
          className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1a1308] to-[#0A0E17] p-5"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Quester Plan</p>
          <h4 className="font-serif-mystic text-2xl mb-1">Unlock 8 deeper quests</h4>
          <p className="text-sm text-[#94A3B8] mb-3">
            Composite readings, Vedic charts, astrocartography map and the Compatibility Engine arrive in Phase 2.
          </p>
          <Button
            data-testid="upgrade-btn"
            onClick={() => (window.location.href = "/pricing")}
            className="bg-[#D4AF37] text-[#05070A] hover:bg-[#F3E5AB]"
          >
            Upgrade · $11/mo
          </Button>
        </div>
      )}
    </div>
  );
};
